export const PUBLIC_FORM_REQUEST_FIELD_TYPE_ID = 28;
export const PUBLIC_FORM_REQUEST_FIELD_TYPE = 'public_form_request';

export const PUBLIC_FORM_REQUEST_CHANNELS = Object.freeze(['email', 'sms']);

const cleanText = (value, maxLength = 120) => Array.from(String(value ?? ''))
    .map((character) => {
        const code = character.charCodeAt(0);
        return code < 32 || code === 127 ? ' ' : character;
    })
    .join('')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, maxLength);

const positiveInteger = (value, fallback, maximum) => {
    const parsed = Number.parseInt(value, 10);
    if (!Number.isInteger(parsed) || parsed < 1) return fallback;
    return Math.min(parsed, maximum);
};

const parseObject = (value) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) return value;
    if (typeof value !== 'string' || !value.trim()) return {};
    try {
        const parsed = JSON.parse(value);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
        return {};
    }
};

const normalizeChannels = (value, useDefaults = true) => {
    let rawChannels = [];
    if (Array.isArray(value)) {
        rawChannels = value;
    } else if (typeof value === 'string') {
        try {
            const parsed = JSON.parse(value);
            rawChannels = Array.isArray(parsed) ? parsed : value.split(',');
        } catch {
            rawChannels = value.split(',');
        }
    }
    const channels = [...new Set(rawChannels
        .map((channel) => String(channel || '').trim().toLowerCase())
        .filter((channel) => PUBLIC_FORM_REQUEST_CHANNELS.includes(channel)))];
    return channels.length > 0 || !useDefaults ? channels : [...PUBLIC_FORM_REQUEST_CHANNELS];
};

export const createPublicFormRequestFieldConfig = () => ({
    form_id: '',
    target_mode: '',
    button_label: 'Send form',
    default_channels: [...PUBLIC_FORM_REQUEST_CHANNELS],
    expires_in_days: 7,
    max_uses: 1,
    unlimited_submissions: false,
});

export const parsePublicFormRequestFieldConfig = (value) => {
    const config = parseObject(value);
    const formId = Number.parseInt(config.form_id ?? config.public_form_id, 10);
    const targetMode = String(config.target_mode ?? config.entity_mode ?? '').toLowerCase();
    const hasExplicitChannels = Object.prototype.hasOwnProperty.call(config, 'default_channels')
        || Object.prototype.hasOwnProperty.call(config, 'channels')
        || Object.prototype.hasOwnProperty.call(config, 'delivery_channels');
    return {
        form_id: Number.isInteger(formId) && formId > 0 ? formId : '',
        target_mode: ['existing_case', 'existing_lead'].includes(targetMode)
            ? targetMode
            : '',
        button_label: cleanText(
            config.button_label ?? config.buttonLabel ?? config.label,
            80,
        ) || 'Send form',
        default_channels: normalizeChannels(
            config.default_channels ?? config.channels ?? config.delivery_channels,
            !hasExplicitChannels,
        ),
        expires_in_days: positiveInteger(
            config.expires_in_days ?? config.expiresInDays ?? config.expiration_days,
            7,
            3650,
        ),
        max_uses: positiveInteger(config.max_uses ?? config.maxUses, 1, 1000),
        unlimited_submissions: config.unlimited_submissions === true
            || config.unlimitedSubmissions === true,
    };
};

export const isPublicFormRequestFieldType = (value) => {
    if (Number(value) === PUBLIC_FORM_REQUEST_FIELD_TYPE_ID) return true;
    return String(value || '').trim().toLowerCase().replace(/[\s-]+/g, '_')
        === PUBLIC_FORM_REQUEST_FIELD_TYPE;
};

export const canUseCustomFieldInDataReport = (field) => (
    !isPublicFormRequestFieldType(field?.field_id)
);

export const isReusablePublicForm = (form) => {
    const mode = String(form?.entity_mode ?? form?.target_mode ?? '').toLowerCase();
    return Number(form?.id) > 0
        && String(form?.status || '').toLowerCase() === 'published'
        && form?.publication_current !== false
        && form?.requires_republish !== true
        && ['existing_case', 'existing_lead'].includes(mode);
};

const normalizeHistoryItem = (item, index) => {
    if (!item || typeof item !== 'object') return null;
    const id = Number(item.id ?? item.request_id ?? item.link_id ?? 0);
    const requestStatus = cleanText(item.status ?? item.request_status, 40).toLowerCase() || 'sent';
    const sentAt = item.sent_at
        ?? item.requested_at
        ?? item.created_at
        ?? item.creation_date
        ?? null;
    const completedAt = item.completed_at
        ?? item.submitted_at
        ?? item.response_at
        ?? item.submission?.submitted_at
        ?? null;
    const directChannels = normalizeChannels(
        item.channels ?? item.requested_channels ?? item.delivery_channels,
        false,
    );
    const deliveryChannels = Array.isArray(item.deliveries)
        ? normalizeChannels(item.deliveries.map((delivery) => delivery?.channel), false)
        : [];
    const deliveryStatuses = Array.isArray(item.deliveries)
        ? item.deliveries.map((delivery) => String(delivery?.status || '').toLowerCase())
        : [];
    const status = !completedAt && deliveryStatuses.some((deliveryStatus) => (
        ['unknown', 'confirmation_pending'].includes(deliveryStatus)
    ))
        ? 'delivery_unknown'
        : !completedAt && deliveryStatuses.some((deliveryStatus) => ['queued', 'sending'].includes(deliveryStatus))
            ? 'dispatching'
            : requestStatus;
    return {
        ...item,
        id: id > 0 ? id : `request-${index}`,
        status,
        request_status: requestStatus,
        sent_at: sentAt,
        completed_at: completedAt,
        channels: directChannels.length > 0 ? directChannels : deliveryChannels,
        recipient_name: cleanText(
            item.recipient_name ?? item.contact_name ?? item.recipient?.name,
            160,
        ),
    };
};

export const normalizeMatterPublicFormContext = (payload) => {
    const data = payload?.context && typeof payload.context === 'object'
        ? payload.context
        : payload?.data && typeof payload.data === 'object'
            ? payload.data
            : payload && typeof payload === 'object'
                ? payload
                : {};
    const rawHistory = Array.isArray(data.history)
        ? data.history
        : Array.isArray(data.requests)
            ? data.requests
            : [];
    const history = rawHistory.map(normalizeHistoryItem).filter(Boolean);
    const rawLatestSource = data.latest_request ?? data.latest ?? data.request ?? history[0] ?? null;
    const latestSource = rawLatestSource && Array.isArray(data.deliveries) && !Array.isArray(rawLatestSource.deliveries)
        ? { ...rawLatestSource, deliveries: data.deliveries }
        : rawLatestSource;
    const latestRequest = latestSource
        ? normalizeHistoryItem(latestSource, 0)
        : null;
    if (latestRequest && !history.some((item) => String(item.id) === String(latestRequest.id))) {
        history.unshift(latestRequest);
    }
    return {
        form: data.form && typeof data.form === 'object' ? data.form : null,
        recipients: Array.isArray(data.recipients) ? data.recipients : [],
        history,
        latest_request: latestRequest,
        can_send: data.can_send !== false,
        unavailable_reason: cleanText(
            data.unavailable_reason ?? data.disabled_reason ?? data.message,
            300,
        ),
    };
};

export const publicFormRequestIsCompleted = (request) => Boolean(
    request?.completed_at
    || request?.submitted_at
    || ['completed', 'submitted', 'approved'].includes(String(request?.status || '').toLowerCase())
);

export const publicFormRequestShouldPoll = (request) => {
    if (!request || publicFormRequestIsCompleted(request)) return false;
    return ![
        'failed',
        'delivery_failed',
        'partial_failed',
        'unknown',
        'delivery_unknown',
        'confirmation_pending',
        'expired',
        'revoked',
        'cancelled',
    ].includes(String(request.status || '').toLowerCase());
};

export const buildMatterPublicFormRequestPayload = ({
    field,
    leadId,
    caseId,
    groupId = null,
}) => {
    const config = parsePublicFormRequestFieldConfig(field?.options);
    const numericLeadId = Number(leadId);
    const numericCaseId = Number(caseId);
    const targetMode = config.target_mode
        || (Number.isInteger(numericCaseId) && numericCaseId > 0 ? 'existing_case' : 'existing_lead');
    const hasGroupId = groupId !== null && groupId !== undefined && groupId !== '';
    const numericGroupId = hasGroupId ? Number(groupId) : Number.NaN;
    return {
        public_form_id: config.form_id || null,
        field_id: Number(field?.id) > 0 ? Number(field.id) : null,
        lead_id: targetMode === 'existing_lead' && Number.isInteger(numericLeadId) && numericLeadId > 0
            ? numericLeadId
            : null,
        case_id: targetMode === 'existing_case' && Number.isInteger(numericCaseId) && numericCaseId > 0
            ? numericCaseId
            : null,
        group_id: Number.isInteger(numericGroupId) && numericGroupId >= 0 ? numericGroupId : null,
        channels: config.default_channels,
        expires_in_days: config.expires_in_days,
        unlimited_submissions: config.unlimited_submissions,
        ...(config.unlimited_submissions ? {} : { max_uses: config.max_uses }),
    };
};
